import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, ChevronDown } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const LanguageSelector: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { lang, setLang } = useLanguage();

  const languages = [
    { code: 'en', label: 'English', flagCode: 'gb' },
    { code: 'tr', label: 'Türkçe', flagCode: 'tr' },
    { code: 'es', label: 'Español', flagCode: 'es' },
    { code: 'de', label: 'Deutsch', flagCode: 'de' },
    { code: 'zh', label: '中文', flagCode: 'cn' },
    { code: 'ja', label: '日本語', flagCode: 'jp' },
    { code: 'ko', label: '한국어', flagCode: 'kr' },
  ];

  const currentLang = languages.find(l => l.code === lang) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderFlag = (flagCode: string) => (
    <img 
      src={`https://flagcdn.com/w40/${flagCode}.png`}
      srcSet={`https://flagcdn.com/w80/${flagCode}.png 2x`}
      width="20"
      alt=""
      className="rounded-[3px] shadow-[0_0_2px_rgba(0,0,0,0.3)] ring-1 ring-white/10"
    />
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2.5 px-4 py-2 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 hover:text-white transition-all text-sm font-bold group backdrop-blur-md"
      >
        <div className="w-5 h-5 flex items-center justify-center rounded-full bg-orange-500/10 text-orange-400 group-hover:scale-110 transition-transform">
          <Globe className="w-3.5 h-3.5" />
        </div>
        <span className="flex items-center gap-2">
          {renderFlag(currentLang.flagCode)}
          <span className="hidden sm:inline tracking-tight">{currentLang.label}</span>
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-500 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="absolute right-0 mt-3 w-48 bg-[#0D0D12]/90 backdrop-blur-3xl rounded-2xl border border-white/10 shadow-2xl shadow-black/80 overflow-hidden z-[100]"
          >
            <div className="p-1.5 flex flex-col gap-1">
              {languages.map((l) => (
                <button
                  key={l.code}
                  onClick={() => {
                    setLang(l.code as any);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm transition-all ${
                    lang === l.code 
                      ? 'bg-orange-500/20 text-orange-400 font-extrabold border border-orange-500/20 shadow-[inset_0_0_12px_rgba(249,115,22,0.1)]' 
                      : 'text-slate-400 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <div className="flex-shrink-0">
                    {renderFlag(l.flagCode)}
                  </div>
                  <span className="tracking-tight">{l.label}</span>
                  {lang === l.code && (
                    <motion.div 
                      layoutId="active-lang"
                      className="ml-auto w-1.5 h-1.5 rounded-full bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.6)]" 
                    />
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSelector;
